import { client, handleRequest, handleRequestBody } from "./client.ts";
import type {
	TimeEntry,
	TimeEntryEdit,
	TimeEntryWorker,
} from "./time-entries.ts";

export type AttendanceDayStatus =
	| "present"
	| "absent"
	| "medical_leave"
	| "day_off"
	| "not_scheduled"
	| "pending"
	| "open";

export type AttendanceDayKind = "weekday" | "saturday" | "sunday" | "holiday";

export interface AttendanceDayTotals {
	worked_minutes: number;
	expected_minutes: number;
	overtime_minutes: number;
	missing_minutes: number;
	overtime_pct: number;
	overtime_equivalent_minutes: number;
}

export interface AttendanceDayAttachment {
	id: string;
	file_name: string;
	content_type: string;
	size_bytes: number;
	uploaded_by_user_id: string | null;
	created_at: string;
}

export interface AttendanceDayRecord {
	id: string;
	status: AttendanceDayStatus;
	note: string;
	overtime_minutes_override: number | null;
	overtime_pct_override: number | null;
	edited: TimeEntryEdit | null;
	attachments: AttendanceDayAttachment[];
	created_at: string;
	updated_at: string;
}

export interface AttendanceDay {
	date: string;
	kind: AttendanceDayKind;
	status: AttendanceDayStatus;
	entries: TimeEntry[];
	record: AttendanceDayRecord | null;
	totals: AttendanceDayTotals;
}

export interface AttendanceWorkerDays {
	worker: TimeEntryWorker;
	days: AttendanceDay[];
}

export interface ListAttendanceDaysParams {
	from: string;
	to: string;
	workerId?: string;
}

export interface ListAttendanceDaysResponse {
	from: string;
	to: string;
	items: AttendanceWorkerDays[];
}

function attendancePath(worksiteId: string, suffix = ""): string {
	return `/worksites/${worksiteId}/attendance${suffix}`;
}

function dayPath(worksiteId: string, workerId: string, date: string): string {
	return attendancePath(worksiteId, `/${workerId}/${date}`);
}

export function listAttendanceDays(
	worksiteId: string,
	params: ListAttendanceDaysParams,
): Promise<ListAttendanceDaysResponse> {
	return handleRequestBody(() =>
		client.get<ListAttendanceDaysResponse>(attendancePath(worksiteId), {
			params: {
				from: params.from,
				to: params.to,
				worker_id: params.workerId,
			},
		}),
	);
}

export interface AttendancePendingWorker {
	worker: TimeEntryWorker;
	dates: string[];
}

export interface ListPendingAttendanceParams {
	from?: string;
	to?: string;
}

export interface ListPendingAttendanceResponse {
	from: string;
	to: string;
	count: number;
	workers: AttendancePendingWorker[];
}

export function listPendingAttendance(
	worksiteId: string,
	params: ListPendingAttendanceParams = {},
): Promise<ListPendingAttendanceResponse> {
	return handleRequestBody(() =>
		client.get<ListPendingAttendanceResponse>(
			attendancePath(worksiteId, "/pending"),
			{ params: { from: params.from, to: params.to } },
		),
	);
}

export function listMyAttendanceDays(
	worksiteId: string,
	params: Omit<ListAttendanceDaysParams, "workerId">,
): Promise<ListAttendanceDaysResponse> {
	return handleRequestBody(() =>
		client.get<ListAttendanceDaysResponse>(attendancePath(worksiteId, "/me"), {
			params: { from: params.from, to: params.to },
		}),
	);
}

export interface PutAttendanceDayInput {
	status: AttendanceDayStatus;
	note: string;
	overtimeMinutesOverride: number | null;
	overtimePctOverride: number | null;
	editNote: string;
}

interface AttendanceDayResponse {
	day: AttendanceDay;
}

export async function putAttendanceDay(
	worksiteId: string,
	workerId: string,
	date: string,
	input: PutAttendanceDayInput,
): Promise<AttendanceDay> {
	const { day } = await handleRequestBody(() =>
		client.put<AttendanceDayResponse>(dayPath(worksiteId, workerId, date), {
			status: input.status,
			note: input.note,
			overtime_minutes_override: input.overtimeMinutesOverride,
			overtime_pct_override: input.overtimePctOverride,
			edit_note: input.editNote,
		}),
	);
	return day;
}

interface AttendanceAttachmentResponse {
	attachment: AttendanceDayAttachment;
}

export async function uploadAttendanceAttachment(
	worksiteId: string,
	workerId: string,
	date: string,
	file: File,
): Promise<AttendanceDayAttachment> {
	const form = new FormData();
	form.append("file", file);
	const { attachment } = await handleRequestBody(() =>
		client.post<AttendanceAttachmentResponse>(
			`${dayPath(worksiteId, workerId, date)}/attachments`,
			form,
		),
	);
	return attachment;
}

export async function attendanceAttachmentUrl(
	worksiteId: string,
	workerId: string,
	date: string,
	attachmentId: string,
): Promise<string> {
	const response = await handleRequest(() =>
		client.get<Blob>(
			`${dayPath(worksiteId, workerId, date)}/attachments/${attachmentId}`,
			{ responseType: "blob" },
		),
	);
	return URL.createObjectURL(response.data);
}

export function deleteAttendanceAttachment(
	worksiteId: string,
	workerId: string,
	date: string,
	attachmentId: string,
): Promise<void> {
	return handleRequest(async () => {
		await client.delete(
			`${dayPath(worksiteId, workerId, date)}/attachments/${attachmentId}`,
		);
	});
}
